import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { Home as HomeIcon, Upload, LogOut, Bell, Radio } from 'lucide-react';
import { useAnalysisContext } from '../contexts/AnalysisContext';
import { useUser } from '../contexts/UserContext';
import SignInWithTwitchButton from './SignInWithTwitchButton';
import { BrandLogo } from './BrandLogo';
import { lscPath } from '../lsc/routing';
import {
  SHOW_FILM_UPLOAD_ENTRY_POINTS,
  SHOW_STREAM_CENTER_ENTRY_POINTS,
} from '../utils/engineAccess';

function userInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user, logout } = useUser();
  const { activeAnalyses, markAllRead } = useAnalysisContext();
  const unreadCount = activeAnalyses.filter((a) => a.unread && a.status === 'completed').length;

  const navItems = [
    { path: '/', label: 'Home', icon: HomeIcon },
    ...(SHOW_FILM_UPLOAD_ENTRY_POINTS
      ? [{ path: '/upload', label: 'Upload', icon: Upload }]
      : SHOW_STREAM_CENTER_ENTRY_POINTS
        ? [{ path: lscPath(), label: 'Stream Center', icon: Radio }]
        : []),
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
        setIsDarkMode(true);
      } else {
        document.documentElement.classList.remove('dark');
        setIsDarkMode(false);
      }
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark');
        setIsDarkMode(true);
      }
    }
  }, []);

  const toggleTheme = () => {
    try {
      const newDarkMode = !isDarkMode;
      setIsDarkMode(newDarkMode);

      if (newDarkMode) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
      }
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
      const newDarkMode = !isDarkMode;
      setIsDarkMode(newDarkMode);

      if (newDarkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
  };

  const accountLabel = user
    ? user.display_name || `@${user.twitch_login}`
    : 'Account';

  return (
    <nav className="bg-white dark:bg-secondary-900 shadow-lg border-2 border-primary-400 dark:border-primary-400">
      <div className="w-full px-6">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex shrink-0 items-center min-w-0" aria-label="Scoutlytics home">
            <BrandLogo variant="brand" className="shrink-0" />
          </Link>
          <div className="hidden md:flex space-x-8">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg transition-colors duration-200 ${
                    isActive
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-400 dark:text-white'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:text-white dark:hover:bg-secondary-800'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={markAllRead}
              className="relative p-2 rounded-lg text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:text-white dark:hover:bg-secondary-800 transition-colors duration-200"
              title={unreadCount > 0 ? `${unreadCount} analysis ready` : 'Notifications'}
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>
            {user ? (
              <>
                <Link
                  to="/profile"
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:text-white dark:hover:bg-secondary-800 transition-colors duration-200"
                  title="Account"
                >
                  {user.profile_image_url ? (
                    <img
                      src={user.profile_image_url}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <span className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-xs font-semibold text-purple-700 dark:text-purple-200">
                      {userInitials(user.display_name || user.twitch_login)}
                    </span>
                  )}
                  <span className="font-medium">{accountLabel}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg text-secondary-600 hover:text-red-600 hover:bg-red-50 dark:text-secondary-300 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors duration-200"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="font-medium">Sign out</span>
                </button>
              </>
            ) : (
              <SignInWithTwitchButton variant="nav" />
            )}
          </div>
          <button
            onClick={toggleTheme}
            className="ml-4 p-2 rounded-full bg-primary-100 dark:bg-secondary-800 hover:bg-primary-200 dark:hover:bg-secondary-700 transition-colors"
            aria-label="Toggle dark mode"
          >
            <svg className="h-5 w-5 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 3v1m0 16v1m8.66-13.66l-.71.71M4.05 19.07l-.71.71M21 12h-1M4 12H3m16.66 5.66l-.71-.71M4.05 4.93l-.71-.71" />
              <circle cx="12" cy="12" r="5" />
            </svg>
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
